import { supabase } from './supabase'

// ============================================
// WHITE LABEL TENANT SYSTEM
// ============================================

export interface Organization {
  id: string
  slug: string
  name: string
  plan_level: 1 | 2
  
  // Branding
  logo_sidebar_url: string | null
  logo_login_url: string | null
  login_background_url: string | null
  favicon_url: string | null
  primary_color: string
  secondary_color: string
  
  // Textos
  login_title: string | null
  login_subtitle: string | null
  
  // Dominio
  custom_domain: string | null
  
  // Config
  settings: Record<string, unknown>
  is_active: boolean
}

export interface OrgTemplate {
  id: string
  org_id: string
  name: string
  description: string | null
  saludos: string[]
  category: 'socio' | 'cliente' | null
  classification: 'confianza' | 'actitud' | 'perfil_se' | null
  is_default: boolean
  is_active: boolean
}

export interface OrgAgent {
  id: string
  org_id: string
  name: string
  description: string | null
  system_prompt: string
  model: string
  temperature: number
  max_tokens: number
  agent_type: string
  is_default: boolean
  is_active: boolean
}

// Organización por defecto (fallback)
const DEFAULT_ORG: Organization = {
  id: 'default',
  slug: 'kin-riman',
  name: 'KIN by Riman',
  plan_level: 2,
  logo_sidebar_url: '/logogris.png',
  logo_login_url: '/aspronet-media/labs.png',
  login_background_url: '/aspronet-media/Fondo-aspronet.png',
  favicon_url: null,
  primary_color: '#2563eb',
  secondary_color: '#7c3aed',
  login_title: 'Bienvenido a KIN',
  login_subtitle: 'Plataforma de IA para tu negocio MLM',
  custom_domain: null,
  settings: {},
  is_active: true,
}

// Cache del tenant actual
let cachedOrg: Organization | null = null
let cacheTimestamp: number = 0
const CACHE_TTL = 5 * 60 * 1000 // 5 minutos

/**
 * Extrae el slug del tenant desde la URL o variable de entorno
 */
function extractTenantSlug(): string {
  // 1. Primero chequeamos variable de entorno (para desarrollo)
  const envSlug = import.meta.env.VITE_TENANT_SLUG
  if (envSlug) {
    console.log('[Tenant] Using env slug:', envSlug)
    return envSlug
  }
  
  // 2. Extraer subdominio del hostname
  const hostname = window.location.hostname
  
  // Localhost o IP -> usar default
  if (hostname === 'localhost' || hostname === '127.0.0.1' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    console.log('[Tenant] Localhost detected, using default: kin-riman')
    return 'kin-riman'
  }
  
  // Subdominio de dominio principal (ej: cliente1.kinplatform.com)
  const parts = hostname.split('.')
  if (parts.length >= 3) {
    console.log('[Tenant] Using subdomain:', parts[0])
    return parts[0] // Primer segmento es el tenant
  }
  
  // Dominio personalizado -> se buscará por custom_domain
  console.log('[Tenant] Using hostname as domain:', hostname)
  return hostname
}

/**
 * Resuelve el tenant actual basado en URL/subdominio
 */
export async function resolveTenant(): Promise<Organization> {
  // Verificar cache
  const now = Date.now()
  if (cachedOrg && (now - cacheTimestamp) < CACHE_TTL) {
    return cachedOrg
  }
  
  const slugOrDomain = extractTenantSlug()
  
  try {
    console.log('[Tenant] Searching for slug:', slugOrDomain)
    
    // Buscar por slug o dominio personalizado
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .or(`slug.eq.${slugOrDomain},custom_domain.eq.${slugOrDomain}`)
      .eq('is_active', true)
      .single()
    
    if (error) {
      console.error('[Tenant] DB Error:', error)
    }
    
    if (error || !data) {
      console.warn(`[Tenant] "${slugOrDomain}" not found in DB, using default`)
      cachedOrg = DEFAULT_ORG
      cacheTimestamp = now
      return DEFAULT_ORG
    }
    
    console.log('[Tenant] Found org:', data.name, data.slug)
    
    cachedOrg = data as Organization
    cacheTimestamp = now
    return cachedOrg
    
  } catch (err) {
    console.error('Error resolving tenant:', err)
    return DEFAULT_ORG
  }
}

/**
 * Obtiene el tenant actual de forma síncrona (del cache)
 * Usar solo después de haber llamado a resolveTenant()
 */
export function getCurrentOrg(): Organization {
  return cachedOrg || DEFAULT_ORG
}

/**
 * Limpia el cache del tenant (útil al cambiar de org)
 */
export function clearTenantCache(): void {
  cachedOrg = null
  cacheTimestamp = 0
}

/**
 * Obtiene los templates precargados de la organización (Nivel 2)
 */
export async function getOrgTemplates(orgId: string): Promise<OrgTemplate[]> {
  const { data, error } = await supabase
    .from('org_templates')
    .select('*')
    .eq('org_id', orgId)
    .eq('is_active', true)
    .order('is_default', { ascending: false })
  
  if (error) {
    console.error('Error fetching org templates:', error)
    return []
  }
  
  return data || []
}

/**
 * Obtiene los agentes precargados de la organización (Nivel 2)
 */
export async function getOrgAgents(orgId: string): Promise<OrgAgent[]> {
  const { data, error } = await supabase
    .from('org_agents')
    .select('*')
    .eq('org_id', orgId)
    .eq('is_active', true)
    .order('is_default', { ascending: false })
  
  if (error) {
    console.error('Error fetching org agents:', error)
    return []
  }
  
  return data || []
}

/**
 * Obtiene el org_id del usuario actual
 */
export async function getUserOrgId(userId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('users')
    .select('org_id')
    .eq('id', userId)
    .single()
  
  if (error || !data) return null
  return data.org_id
}

/**
 * Asigna un usuario a una organización
 */
export async function assignUserToOrg(userId: string, orgId: string): Promise<boolean> {
  const { error } = await supabase
    .from('users')
    .update({ org_id: orgId })
    .eq('id', userId)
  
  if (error) {
    console.error('Error assigning user to org:', error)
    return false
  }
  return true
}

/**
 * Copia los templates de la org al usuario (para Nivel 2)
 */
export async function copyOrgTemplatesToUser(
  orgId: string, 
  userId: string
): Promise<boolean> {
  try {
    const orgTemplates = await getOrgTemplates(orgId)
    
    if (orgTemplates.length === 0) return true
    
    // Insertar en message_templates del usuario
    const templatesToInsert = orgTemplates.map(t => ({
      user_id: userId,
      name: t.name,
      description: t.description,
      saludos: t.saludos,
      category: t.category,
      classification: t.classification,
      is_default: t.is_default,
      // Marcar como copiado de org
      metadata: { copied_from_org: orgId, original_template_id: t.id }
    }))
    
    const { error } = await supabase
      .from('message_templates')
      .insert(templatesToInsert)
    
    if (error) {
      console.error('Error copying org templates:', error)
      return false
    }
    
    return true
  } catch (err) {
    console.error('Error in copyOrgTemplatesToUser:', err)
    return false
  }
}

/**
 * Copia los agentes de la org al usuario (para Nivel 2)
 */
export async function copyOrgAgentsToUser(
  orgId: string, 
  userId: string
): Promise<boolean> {
  try {
    const orgAgents = await getOrgAgents(orgId)
    
    if (orgAgents.length === 0) return true
    
    // Insertar en agents del usuario
    const agentsToInsert = orgAgents.map(a => ({
      user_id: userId,
      name: a.name,
      description: a.description,
      system_prompt: a.system_prompt,
      model: a.model,
      temperature: a.temperature,
      max_tokens: a.max_tokens,
      agent_type: a.agent_type,
      is_active: true,
      // Marcar como copiado de org
      metadata: { copied_from_org: orgId, original_agent_id: a.id }
    }))
    
    const { error } = await supabase
      .from('agents')
      .insert(agentsToInsert)
    
    if (error) {
      console.error('Error copying org agents:', error)
      return false
    }
    
    return true
  } catch (err) {
    console.error('Error in copyOrgAgentsToUser:', err)
    return false
  }
}

/**
 * Verifica si la organización tiene plan Nivel 2
 */
export function hasLevel2Features(org: Organization): boolean {
  return org.plan_level === 2
}
